import dayjs from 'dayjs';

import { getErrorMessage } from 'shared/helpers/getErrorMessage';

import { addNotify } from 'features/notify';
import { customRequestTypesEnums } from 'features/creativeFunctionality/models/customRequestsModel/data';

const actionTypes = {
  ACTION_PROCESSING: 'creativeFunctionality/ACTION_PROCESSING',
  GET_CUSTOM_REQUESTS: 'creativeFunctionality/GET_CUSTOM_REQUESTS',
  GET_CUSTOM_REQUESTS_NOTIFY: 'creativeFunctionality/GET_CUSTOM_REQUESTS_NOTIFY',
  GET_CUSTOM_PROVIDERS: 'creativeFunctionality/GET_CUSTOM_PROVIDERS',
  REMOVE_CUSTOM_REQUEST: 'creativeFunctionality/REMOVE_CUSTOM_REQUEST',
  SAVE_PREV_CUSTOM_PROVIDER: 'creativeFunctionality/SAVE_PREV_CUSTOM_PROVIDER',
  GET_SOCIAL_MEDIA: 'creativeFunctionality/GET_SOCIAL_MEDIA',
  GET_CUSTOM_GAMES: 'creativeFunctionality/GET_CUSTOM_GAMES',
  SAVE_PREV_CUSTOM_GAMES: 'creativeFunctionality/SAVE_PREV_CUSTOM_GAMES',
  GET_SINGLE_CUSTOM_REQUESTS: 'creativeFunctionality/GET_SINGLE_CUSTOM_REQUESTS',
  CLEAR_SINGLE_CUSTOM_DATA: 'creativeFunctionality/CLEAR_SINGLE_CUSTOM_DATA',
};

function getCustomRequests(formData, type) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.getCustomRequests(formData);
    if (response.success) {
      if (type === customRequestTypesEnums.notifications) {
        dispatch({
          type: actionTypes.GET_CUSTOM_REQUESTS_NOTIFY,
          payload: response.data,
        });
      } else {
        dispatch({
          type: actionTypes.GET_CUSTOM_REQUESTS,
          payload: response.data,
        });
      }
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }
    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getSingleCustomRequest(id, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    dispatch({
      type: actionTypes.CLEAR_SINGLE_CUSTOM_DATA,
    });

    const response = await api.creativeFunctionality.getSingleCustomRequest(id);
    if (response.success) {
      dispatch({
        type: actionTypes.GET_SINGLE_CUSTOM_REQUESTS,
        payload: response.data,
      });

      if (callback) callback(response.data);
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }
    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function removeCustomRequest(id) {
  return async (dispatch, getState, extra) => {
    dispatch({
      type: actionTypes.REMOVE_CUSTOM_REQUEST,
      payload: id,
    });
  };
}

function approveCustomRequest(data, callback = null) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.approveCustomRequest(data);

    if (response.success) {
      callback && callback();
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function deleteCustomRequest(data, callback = null) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.deleteCustomRequest(data);

    if (response.success) {
      callback && callback();
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getCustomProviders() {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.getCustomProviders();

    if (response.success) {
      dispatch({
        type: actionTypes.GET_CUSTOM_PROVIDERS,
        payload: response.data,
      });
      dispatch(savePreviousCustomProviders(response.data?.items ?? []));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getCustomGames() {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.getCustomGames();

    if (response.success) {
      dispatch({
        type: actionTypes.GET_CUSTOM_GAMES,
        payload: response.data,
      });
      dispatch(savePreviousCustomGames(response.data ?? []));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getSocialMedia() {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.getSocialMedia();

    if (response.success) {
      dispatch({
        type: actionTypes.GET_SOCIAL_MEDIA,
        payload: response.data,
      });
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function setSocialRequest(data) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.setSocialRequest(data);

    if (response.success) {
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function savePreviousCustomProviders(providers) {
  return async (dispatch, getState, extra) => {
    dispatch({
      type: actionTypes.SAVE_PREV_CUSTOM_PROVIDER,
      payload: providers,
    });
  };
}

function savePreviousCustomGames(games) {
  return async (dispatch, getState, extra) => {
    dispatch({
      type: actionTypes.SAVE_PREV_CUSTOM_GAMES,
      payload: games,
    });
  };
}

function setCustomProviders(providers, activeProviders) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.setCustomProviders(providers);

    if (response.success) {
      dispatch(savePreviousCustomProviders(activeProviders));
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function setCustomGames(games, activeGames) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.setCustomGames(games);

    if (response.success) {
      dispatch(savePreviousCustomGames(activeGames));
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function createCustomNotification(data) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { locale } = getState().locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const response = await api.creativeFunctionality.createCustomNotification(data);

    if (response.success) {
      dispatch(
        getCustomRequests(
          {
            status: 'created',
            type: 'notification',
            date_from: dayjs().add(-1, 'month').format('YYYY-MM-DD'),
            date_to: dayjs().format('YYYY-MM-DD'),
            is_my: true,
          },
          'notification',
        ),
      );
      // dispatch(removeCustomRequest(data.id));
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

export {
  actionTypes,
  getCustomRequests,
  approveCustomRequest,
  getSingleCustomRequest,
  removeCustomRequest,
  getCustomProviders,
  setCustomProviders,
  createCustomNotification,
  getSocialMedia,
  setSocialRequest,
  deleteCustomRequest,
  getCustomGames,
  setCustomGames,
};
